import wikiData from '@/data/wikiLoadoutData'
import gameIcons from '@/data/gameIconManifest.json'
import { getKillerProfile } from '@/data/analysisProfiles'
import { createSurvivorCatalog } from '@/data/survivorPerks'
import { canonicalSignals } from '@/lib/loadoutCorrelation'
import { getSurvivorInteraction } from '@/data/survivorPerkInteractions'

const SURVIVOR_METADATA = new Map(createSurvivorCatalog([]).reduce((entries, perk) => {
  entries.push([perk.id, perk])
  if (perk.wikiId) entries.push([perk.wikiId, perk])
  return entries
}, []))

const COMPLEMENTS = {
  information: ['generator', 'chase', 'hook', 'totem', 'stealth', 'endgame'],
  generator: ['information', 'item', 'stealth'],
  healing: ['item', 'hook', 'stealth', 'information'],
  chase: ['information', 'stealth', 'item'],
  stealth: ['information', 'chase', 'healing', 'endgame'],
  item: ['generator', 'healing', 'loot', 'sabotage', 'chase'],
  loot: ['item', 'information'],
  hook: ['healing', 'information', 'chase'],
  totem: ['information', 'generator', 'endgame'],
  sabotage: ['item', 'hook', 'chase'],
  endgame: ['information', 'stealth', 'chase'],
  obsession: ['chase', 'information'],
  power: ['information', 'chase', 'hook'],
  bloodpoints: []
}

const DIMINISHING_TAGS = [
  'actionspeed', 'actiontime', 'healingspeed', 'healingproficiency', 'repairingproficiency',
  'recoveryspeed', 'movementspeed', 'haste', 'hindered', 'skillchecks', 'skillcheckchance',
  'cleansingspeed', 'vaulting', 'breaking', 'damaging', 'sabotageproficiency'
]

const TYPE_ORDER = { character: 0, perk: 1, item: 2, addon: 3, offering: 4 }

function normalizeTag (value) {
  return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '')
}

function unique (values) {
  return Array.from(new Set((values || []).filter(Boolean)))
}

function iconFor (type, side, entry) {
  if (type === 'character') return ((gameIcons.characters[side] || {})[String(entry.id)]) || ''
  if (type === 'item') return gameIcons.items[String(entry.id)] || ''
  if (type === 'addon') return gameIcons.addons[String(entry.id)] || ''
  if (type === 'offering') return gameIcons.offerings[String(entry.id)] || ''
  return ''
}

function baseEntity (entry, type, side, subtype) {
  return {
    key: `${type}:${side}:${entry.id}`,
    id: entry.id,
    name: entry.name,
    type,
    side,
    subtype: subtype || type,
    tags: (entry.tags || []).slice(),
    signals: canonicalSignals(entry.tags || []),
    patch: entry.patch || null,
    values: (entry.values || []).slice(),
    units: (entry.units || []).slice(),
    mechanics: Object.assign({}, entry.mechanics || {}),
    rarity: entry.rarity || null,
    character: entry.character || null,
    category: entry.category || entry.itemsCategory || null,
    icon: iconFor(type, side, entry),
    source: entry
  }
}

function perkEntity (entry, side) {
  const entity = baseEntity(entry, 'perk', side, `${side}Perk`)
  if (side === 'survivor') {
    const metadata = SURVIVOR_METADATA.get(entry.id)
    entity.manualSynergies = Object.assign({}, (metadata && metadata.synergyWeights) || {})
    entity.antiSynergies = Object.assign({}, (metadata && metadata.antiSynergies) || {})
    entity.diminishingGroups = ((metadata && metadata.diminishingGroups) || []).slice()
    entity.strength = (metadata && metadata.strengthScore) || 50
  }
  return entity
}

function characterEntity (entry, side) {
  const entity = baseEntity(entry, 'character', side, `${side}Character`)
  if (side === 'killer') {
    const profile = getKillerProfile(entry)
    entity.tags = profile.tags.slice()
    entity.signals = canonicalSignals(entity.tags)
    entity.profile = profile
  }
  return entity
}

export function createIntelligenceCatalog () {
  const entities = []
  wikiData.survivors.forEach(entry => entities.push(characterEntity(entry, 'survivor')))
  wikiData.killers.forEach(entry => entities.push(characterEntity(entry, 'killer')))
  wikiData.survivorPerks.forEach(entry => entities.push(perkEntity(entry, 'survivor')))
  wikiData.killerPerks.forEach(entry => entities.push(perkEntity(entry, 'killer')))
  wikiData.items.forEach(entry => entities.push(baseEntity(entry, 'item', 'survivor', 'item')))
  wikiData.survivorAddons.forEach(entry => entities.push(baseEntity(entry, 'addon', 'survivor', 'itemAddon')))
  wikiData.killerAddons.forEach(entry => entities.push(baseEntity(entry, 'addon', 'killer', 'powerAddon')))
  wikiData.offerings.forEach(entry => {
    const side = entry.charType === 'K' ? 'killer' : entry.charType === 'S' ? 'survivor' : 'both'
    entities.push(baseEntity(entry, 'offering', side, 'offering'))
  })
  return entities.sort((first, second) => {
    return TYPE_ORDER[first.type] - TYPE_ORDER[second.type] || first.name.localeCompare(second.name)
  })
}

function sideCompatible (first, second) {
  if (first.side === 'both' || second.side === 'both') return true
  return first.side === second.side
}

function boundCompatibility (first, second) {
  const item = first.type === 'item' ? first : second.type === 'item' ? second : null
  const itemAddon = first.subtype === 'itemAddon' ? first : second.subtype === 'itemAddon' ? second : null
  if (item && itemAddon) return item.category === itemAddon.category ? 34 : -100

  const killer = first.subtype === 'killerCharacter' ? first : second.subtype === 'killerCharacter' ? second : null
  const powerAddon = first.subtype === 'powerAddon' ? first : second.subtype === 'powerAddon' ? second : null
  if (killer && powerAddon) return killer.id === powerAddon.character ? 42 : -100
  if (first.subtype === 'powerAddon' && second.subtype === 'powerAddon') {
    return first.character === second.character ? 18 : -100
  }
  return 0
}

function manualRelationship (first, second) {
  const firstWeight = first.manualSynergies && first.manualSynergies[second.id]
  const secondWeight = second.manualSynergies && second.manualSynergies[first.id]
  const mechanic = first.type === 'perk' && second.type === 'perk' && first.side === 'survivor' && second.side === 'survivor'
    ? getSurvivorInteraction(first.id, second.id)
    : null
  const mechanicAnti = mechanic && mechanic.kind === 'anti_synergy'
    ? { severity: mechanic.severity, reason: mechanic.reason }
    : null
  const anti = mechanicAnti || (first.antiSynergies && first.antiSynergies[second.id]) || (second.antiSynergies && second.antiSynergies[first.id])
  return {
    weight: mechanic && typeof mechanic.weight === 'number'
      ? mechanic.weight
      : Math.max(Number(firstWeight) || 0, Number(secondWeight) || 0),
    anti,
    mechanic
  }
}

function sharedModifiers (first, second) {
  const firstTags = new Set(first.tags.map(normalizeTag))
  const secondTags = new Set(second.tags.map(normalizeTag))
  return DIMINISHING_TAGS.filter(tag => firstTags.has(tag) && secondTags.has(tag))
}

function complementarySignals (first, second) {
  const secondSignals = new Set(second.signals)
  return unique(first.signals.reduce((matches, signal) => {
    ;(COMPLEMENTS[signal] || []).forEach(target => {
      if (secondSignals.has(target)) matches.push(`${signal}:${target}`)
    })
    return matches
  }, []))
}

function tierFor (score) {
  if (score >= 88) return 'exceptional'
  if (score >= 74) return 'strong'
  if (score >= 58) return 'useful'
  if (score >= 40) return 'situational'
  return 'conflict'
}

export function analyzeRelationship (first, second) {
  if (!first || !second || first.key === second.key) return null
  const shared = first.signals.filter(signal => second.signals.indexOf(signal) !== -1)
  const complements = complementarySignals(first, second).concat(complementarySignals(second, first))
  const modifiers = sharedModifiers(first, second)
  const binding = boundCompatibility(first, second)
  const manual = manualRelationship(first, second)
  const compatibleSide = sideCompatible(first, second)
  const sharedValue = shared.length * 14
  const complementValue = Math.min(18, complements.length * 5)
  const manualValue = manual.weight ? 20 + manual.weight * 0.18 : 0
  const redundancyValue = first.type === 'perk' && second.type === 'perk' && shared.length >= 3 ? -9 : 0
  const diminishingValue = modifiers.length ? -Math.min(16, modifiers.length * 5) : 0
  const sideValue = !compatibleSide && first.type !== 'offering' && second.type !== 'offering' ? -42 : 0
  const mechanicBlocked = Boolean(manual.mechanic && manual.mechanic.bestBuildBlocked)
  const antiValue = manual.anti ? (manual.anti.severity === 'hard' ? -70 : -34) : mechanicBlocked ? -70 : 0
  let score = 35 + sharedValue + complementValue + binding + manualValue + redundancyValue + diminishingValue + sideValue + antiValue
  const reasons = []
  const warnings = []
  const factors = [
    { id: 'baseline', value: 35, values: [] },
    sharedValue ? { id: 'shared', value: sharedValue, values: shared.slice(0, 4) } : null,
    complementValue ? { id: 'complement', value: complementValue, values: complements.slice(0, 3) } : null,
    binding ? { id: binding > 0 ? 'binding' : 'incompatible', value: binding, values: [] } : null,
    manualValue ? { id: 'curated', value: Math.round(manualValue), values: [String(Math.round(manual.weight))] } : null,
    redundancyValue ? { id: 'redundancy', value: redundancyValue, values: shared.slice(0, 4) } : null,
    diminishingValue ? { id: 'diminishing', value: diminishingValue, values: modifiers } : null,
    sideValue ? { id: 'side', value: sideValue, values: [] } : null,
    antiValue ? {
      id: 'antiSynergy', value: antiValue, values: [(manual.anti && manual.anti.reason) || (manual.mechanic && manual.mechanic.reason) || '']
    } : null
  ].filter(Boolean)
  if (mechanicBlocked) score = 1

  if (binding <= -100) score = 1
  if (binding >= 34) reasons.push({ type: 'bound', values: [] })
  if (manual.weight) reasons.push({ type: 'manual', values: [String(Math.round(manual.weight))] })
  if (shared.length) reasons.push({ type: 'shared', values: shared.slice(0, 4) })
  if (complements.length) reasons.push({ type: 'complement', values: complements.slice(0, 3) })
  if (!reasons.length) reasons.push({ type: 'flex', values: [] })
  if (modifiers.length) warnings.push({ type: 'diminishing', values: modifiers })
  if (redundancyValue) warnings.push({ type: 'redundancy', values: shared })
  if (!compatibleSide) warnings.push({ type: 'side', values: [] })
  if (binding <= -100) warnings.push({ type: 'incompatible', values: [] })
  if (manual.anti) warnings.push({ type: 'antiSynergy', values: [manual.anti.reason || ''] })
  if (mechanicBlocked && !manual.anti) warnings.push({ type: 'antiSynergy', values: [manual.mechanic.reason || ''] })

  const normalizedScore = Math.max(1, Math.min(99, Math.round(score)))
  const evidence = shared.length + complements.length + modifiers.length
  const confidence = binding !== 0
    ? 98
    : manual.weight || manual.anti || manual.mechanic
      ? 96
      : Math.max(46, Math.min(90, 58 + evidence * 7 + (first.patch && second.patch ? 4 : 0)))
  const evidenceStrength = binding !== 0
    ? 'constraint'
    : manual.weight || manual.anti || manual.mechanic
      ? 'curated'
      : evidence >= 4 ? 'documented' : evidence >= 2 ? 'supported' : 'inferred'

  return {
    entity: second,
    score: normalizedScore,
    tier: tierFor(normalizedScore),
    confidence,
    evidenceStrength,
    factors,
    shared,
    complements,
    modifiers,
    reasons,
    warnings
  }
}
function relevantCandidate (focus, candidate) {
  if (focus.key === candidate.key) return false
  if (focus.type === 'character' && focus.side === 'survivor' && candidate.type === 'character') return false
  if (focus.subtype === 'itemAddon') {
    if (candidate.type === 'item') return candidate.category === focus.category
    if (candidate.subtype === 'itemAddon') return candidate.category === focus.category
  }
  if (focus.subtype === 'powerAddon') {
    if (candidate.subtype === 'killerCharacter') return candidate.id === focus.character
    if (candidate.subtype === 'powerAddon') return candidate.character === focus.character
  }
  if (focus.subtype === 'killerCharacter' && candidate.subtype === 'powerAddon') return candidate.character === focus.id
  if (focus.type === 'item' && candidate.subtype === 'itemAddon') return candidate.category === focus.category
  return sideCompatible(focus, candidate) || candidate.type === 'offering'
}

export function rankRelationships (focus, catalog, options) {
  const config = Object.assign({ limit: 18, type: 'all' }, options || {})
  return catalog
    .filter(candidate => relevantCandidate(focus, candidate))
    .filter(candidate => config.type === 'all' || candidate.type === config.type)
    .map(candidate => analyzeRelationship(focus, candidate))
    .filter(Boolean)
    .sort((first, second) => second.score - first.score || second.confidence - first.confidence || first.entity.name.localeCompare(second.entity.name))
    .slice(0, config.limit)
}

export function recentEntries (catalog, limit) {
  const versionParts = value => String(value || '0').split('.').map(Number)
  const compare = (first, second) => {
    const a = versionParts(first.patch)
    const b = versionParts(second.patch)
    for (let index = 0; index < 3; index += 1) {
      if ((a[index] || 0) !== (b[index] || 0)) return (b[index] || 0) - (a[index] || 0)
    }
    return first.name.localeCompare(second.name)
  }
  return catalog.filter(entity => entity.patch).sort(compare).slice(0, limit || 24)
}

export function intelligenceMetrics (catalog) {
  const tagged = catalog.filter(entity => entity.tags.length).length
  const mechanical = catalog.filter(entity => entity.values.length || Object.keys(entity.mechanics).length).length
  return {
    total: catalog.length,
    tagged,
    mechanical,
    coverage: Math.round((tagged / catalog.length) * 100),
    liveVersion: wikiData.liveVersion || 'unknown',
    generatedAt: wikiData.generatedAt
  }
}
